/**
 * RiderModel: the actual Synth Riders Console game character ("Rider") on his
 * hoverboard, replacing the procedural mannequin in the Console editor.
 *
 * Source: `SK_Rider_ANIM.fbx` (Unity project) → exported to GLB via Blender.
 * The GLB is self-contained: the default outfit mesh (Head_01 / Legs_01 /
 * Torso_01 + backpack + Board_03 hoverboard), the shared RIG_Rider skeleton
 * (97 bones), and all of the game's baked animation clips (131 of them).
 *
 * Unlike DancerModel (which animates a static FBX procedurally), this model
 * plays the REAL baked clips through three.js' AnimationMixer:
 *   - paused              → a calm board/idle loop
 *   - playing, no event   → the board-run riding loop
 *   - playing, on note     → the game's directional note-hit reaction pose
 *                            (`A_Rider_TN_<leftDir><rightDir>`)
 *   - playing, on obstacle → a dodge / jump / duck reaction (overrides notes)
 *
 * All reactions are driven imperatively off `playback.currentBeat` (the
 * OFFSET-ADJUSTED chart beat, which matches `note.time_beats`) — never a raw
 * `currentTime/60*bpm`, so offset≠0 maps stay in sync. We read the store via
 * getState() inside useFrame rather than subscribing, so dense maps don't
 * trigger 60fps React re-renders (same imperative pattern as the rest of the
 * scene).
 *
 * Mounted only for the Console editor; VR keeps DancerModel/HumanFigure.
 */

import React, { useRef, useEffect, useMemo } from 'react';
import { useGLTF, useAnimations } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { useEditorStore } from '../../store/editorStore';
import { applyRimGlow } from './consoleShaders';
import { GRID_SCALE } from '../../types/beatmap';
import type { Note, Difficulty } from '../../types/beatmap';

const RIDER_GLB_PATH = '/models/console/SK_Rider.glb';
useGLTF.preload(RIDER_GLB_PATH);

// Match TunnelScene / DancerModel placement at the playhead.
const FIGURE_Z = 0.12;

// Clip-name preferences (matched case-insensitively, by substring, so the
// FBX take prefix or outfit suffix doesn't matter). First match wins.
const IDLE_CLIPS = ['Board_Idle', 'Idle_Male', 'Hub_Rider_Idle', 'Idle'];
const RUN_CLIPS = ['Board_Run', 'Hub_Rider_Run', 'Run', 'Board_Idle', 'Idle_Male'];

// ── Reaction timing (in beats; the AnimationMixer plays clips in real time) ──
// How long a pose lingers after its beat before drifting back to the run loop,
// and how early we anticipate an upcoming event. Note HOLD is tuned > a 1/4
// beat so back-to-back notes flow pose→pose; obstacle reactions (dodge/jump)
// are bigger full-body actions so they hold longer and OVERRIDE notes.
const NOTE_HOLD_BEATS = 0.45;
const OBSTACLE_HOLD_BEATS = 0.7;
const LEAD_BEATS = 0.15;
const FADE = 0.12; // crossfade seconds between any two clips
// Air-rail (floor→air) jump arc windows, in beats.
const AIR_START_BEATS = 0.8; // launch window at the start of an air rail
const AIR_END_BEATS = 0.8;   // landing window at the end
// Rider mounts the rail and follows its flow (matches the game continuously
// translating the character to the rail's current point while grinding).
const GRID_W = GRID_SCALE * 10;   // world units per grid unit (matches TunnelScene)
const RAIL_Y_BASE = 1.8;          // NOTE_Y_OFFSET — rail world-height baseline
const AIR_MOUNT_LIFT = 1.0;       // fraction of rail height the rider rises to (tunable)
const AIR_FOLLOW_LERP = 0.14;     // smoothing toward the rail point (grind-tween feel)

type Dir = 'Up' | 'Down' | 'Left' | 'Right' | 'None';

/**
 * Derive a hit direction from a note's position, mirroring the game's logic
 * (Synth Riders Console NoteData/GameStructs): South (foot) when low, North
 * when high, otherwise East/West by the X sign. The console .synth format does
 * NOT store the `direction` enum (it always loads as 0), so we recompute it.
 */
function dirToken(pos: [number, number, number]): Dir {
  const x = pos[0];
  const y = pos[1];
  if (y <= -0.5) return 'Down';  // South → ground/foot
  if (y >= 0.6) return 'Up';     // North
  if (x <= -0.05) return 'Left'; // West
  if (x >= 0.05) return 'Right'; // East
  return 'None';                 // centre
}

interface AnimEvent {
  beat: number;
  clip: string;
}

/** Resolve a clip whose name ends with `suffix` (excludes the `_Out` partner). */
function resolveClip(names: string[], suffix: string): string | null {
  const want = suffix.toLowerCase();
  return names.find(n => n.toLowerCase().endsWith(want)) ?? null;
}

/**
 * Pre-compute the sorted list of note-hit reaction events for a difficulty.
 * Notes that share a beat (max 2 in practice: one per hand) combine into a
 * single `TN_<leftDir><rightDir>` clip; a one-hand-special note maps to the
 * symmetric `TN_FrontFront`.
 */
function buildNoteEvents(notes: Note[] | undefined, names: string[]): AnimEvent[] {
  if (!notes || !notes.length || !names.length) return [];

  const groups = new Map<number, Note[]>();
  for (const n of notes) {
    const key = Math.round(n.time_beats * 1000) / 1000;
    const arr = groups.get(key);
    if (arr) arr.push(n);
    else groups.set(key, [n]);
  }

  const events: AnimEvent[] = [];
  for (const [beat, arr] of groups) {
    let left: Note | null = null;
    let right: Note | null = null;
    let special = false;
    for (const n of arr) {
      if (n.hand === 'left') left = n;
      else if (n.hand === 'right') right = n;
      else special = true;
    }
    let clip: string | null;
    if (special && !left && !right) {
      clip = resolveClip(names, 'tn_frontfront');
    } else {
      const lt: Dir = left ? dirToken(left.position) : 'None';
      const rt: Dir = right ? dirToken(right.position) : 'None';
      clip = resolveClip(names, ('tn_' + lt + rt).toLowerCase());
    }
    if (clip) events.push({ beat, clip });
  }
  events.sort((a, b) => a.beat - b.beat);
  return events;
}

/**
 * Pre-compute obstacle reaction events. Slides → dodge AWAY from the wall (or
 * a jump for a centre wall); crouch obstacles → a low "duck" pose. These read
 * as bigger full-body actions and take priority over note reactions.
 *   wall_right / angle_right → Dodge_L   (lean left, away from a right wall)
 *   wall_left  / angle_left  → Dodge_R
 *   center                   → JumpShort (hop the central barrier)
 *   crouch                   → Grind (closest low/crouched clip — no dedicated
 *                              duck animation exists in this character set)
 */
function buildObstacleEvents(difficulty: Difficulty | null, names: string[]): AnimEvent[] {
  if (!difficulty || !names.length) return [];

  const dodgeL = resolveClip(names, 'dodge_l');
  const dodgeR = resolveClip(names, 'dodge_r');
  const jump = resolveClip(names, 'jumpshort_start_1')
    || resolveClip(names, 'jumpdefault_start')
    || resolveClip(names, 'jump_start');
  const duck = resolveClip(names, 'grind_1') || resolveClip(names, 'grind_2');

  const events: AnimEvent[] = [];
  for (const s of difficulty.slides || []) {
    const wt = s.wall_type;
    let clip: string | null;
    if (wt === 'wall_right' || wt === 'angle_right') clip = dodgeL;
    else if (wt === 'wall_left' || wt === 'angle_left') clip = dodgeR;
    else if (wt === 'center') clip = jump;
    else clip = dodgeL;
    if (clip) events.push({ beat: s.time_beats, clip });
  }
  for (const c of difficulty.crouch_obstacles || []) {
    if (duck) events.push({ beat: c.time_beats, clip: duck });
  }
  events.sort((a, b) => a.beat - b.beat);
  return events;
}

/** First index whose event beat is >= target (binary search). */
function lowerBound(events: AnimEvent[], target: number): number {
  let lo = 0;
  let hi = events.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid].beat < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// ── Two-bone IK (rail lock) ──────────────────────────────────────────────────
// Analytic 2-bone IK that lands a limb's end bone (hand/foot) on a WORLD target
// by bending the chain root→mid→end, preserving the animated bend plane (so the
// elbow/knee keeps its natural direction). Run AFTER the AnimationMixer each
// frame so it overrides the baked pose while the rider grinds a rail.
const _IK = {
  a: new THREE.Vector3(), b: new THREE.Vector3(), c: new THREE.Vector3(),
  t: new THREE.Vector3(), v1: new THREE.Vector3(), v2: new THREE.Vector3(),
  axis: new THREE.Vector3(), dq: new THREE.Quaternion(), wq: new THREE.Quaternion(), pq: new THREE.Quaternion(),
};
function rotateBoneWorld(bone: THREE.Object3D, deltaWorld: THREE.Quaternion) {
  bone.getWorldQuaternion(_IK.wq);
  _IK.wq.premultiply(deltaWorld);
  if (bone.parent) {
    bone.parent.getWorldQuaternion(_IK.pq);
    bone.quaternion.copy(_IK.pq.invert().multiply(_IK.wq));
  } else {
    bone.quaternion.copy(_IK.wq);
  }
}
function solveTwoBoneIK(root: THREE.Object3D, mid: THREE.Object3D, end: THREE.Object3D, target: THREE.Vector3) {
  // Refresh the WHOLE chain (true,true = update descendants too): the mixer just
  // wrote local poses but left every matrixWorld stale, so mid/end would read
  // last frame's positions without this. Without it the solver chases stale data
  // and the limb never settles on the rail.
  root.updateWorldMatrix(true, true);
  const a = _IK.a.setFromMatrixPosition(root.matrixWorld);
  const b = _IK.b.setFromMatrixPosition(mid.matrixWorld);
  const c = _IK.c.setFromMatrixPosition(end.matrixWorld);
  const l1 = a.distanceTo(b);
  const l2 = b.distanceTo(c);
  if (l1 < 1e-4 || l2 < 1e-4) return;
  const t = _IK.t.copy(target);
  const maxR = (l1 + l2) * 0.999;
  if (a.distanceTo(t) > maxR) t.sub(a).setLength(maxR).add(a);
  const dist = Math.min(a.distanceTo(t), maxR);
  // Bend axis = current pose's elbow-plane normal (keeps the natural bend dir).
  const ab = _IK.v1.copy(b).sub(a);
  const bc = _IK.v2.copy(c).sub(b);
  _IK.axis.crossVectors(ab, bc);
  if (_IK.axis.lengthSq() < 1e-8) _IK.axis.set(0, 0, 1);
  _IK.axis.normalize();
  // 1) Bend the mid joint to the interior angle that puts the end `dist` away.
  const cosMid = THREE.MathUtils.clamp((l1 * l1 + l2 * l2 - dist * dist) / (2 * l1 * l2), -1, 1);
  const desiredMid = Math.acos(cosMid);
  const curMid = _IK.v1.copy(a).sub(b).angleTo(_IK.v2.copy(c).sub(b));
  _IK.dq.setFromAxisAngle(_IK.axis, desiredMid - curMid);
  rotateBoneWorld(mid, _IK.dq);
  // 2) Aim the root so the (now-bent) end points at the target. Re-walk the
  // chain (true,true) so `end` reflects the mid bend we just applied.
  root.updateWorldMatrix(true, true);
  const a2 = _IK.a.setFromMatrixPosition(root.matrixWorld);
  const c2 = _IK.c.setFromMatrixPosition(end.matrixWorld);
  _IK.v1.copy(c2).sub(a2).normalize();
  _IK.v2.copy(t).sub(a2).normalize();
  _IK.dq.setFromUnitVectors(_IK.v1, _IK.v2);
  rotateBoneWorld(root, _IK.dq);
}

// Lean `bone` (a clavicle) PARTWAY so its `endBone` moves toward the target. We
// pass the SHOULDER JOINT as endBone (not the hand) — its position is stable
// (doesn't swing with the arm animation), so the lean is steady. This shifts the
// arm origin toward the rail, extending reach + countering the torso sway that
// otherwise pulls the target out of arm range mid-rail.
function aimBonePartial(bone: THREE.Object3D, endBone: THREE.Object3D, target: THREE.Vector3, frac: number) {
  bone.updateWorldMatrix(true, true);
  const bp = _IK.a.setFromMatrixPosition(bone.matrixWorld);
  const ep = _IK.c.setFromMatrixPosition(endBone.matrixWorld);
  if (ep.distanceTo(bp) < 1e-4) return;
  _IK.v1.copy(ep).sub(bp).normalize();
  _IK.v2.copy(target).sub(bp).normalize();
  _IK.dq.setFromUnitVectors(_IK.v1, _IK.v2);
  _IK.wq.set(0, 0, 0, 1).slerp(_IK.dq, frac); // partial (identity → full aim)
  rotateBoneWorld(bone, _IK.wq);
}

// A limb's rail = a polyline of {beat, worldX, worldY}. Returns the world point
// to lock the hand/foot to at `beat`, or null when off the rail (before head /
// after the last node → limb is free, the animation takes over).
interface LimbRail { beats: number[]; xs: number[]; ys: number[]; }
function sampleLimbRail(r: LimbRail, beat: number): { x: number; y: number } | null {
  const n = r.beats.length;
  if (beat < r.beats[0] || beat >= r.beats[n - 1]) return null;
  let i = 0;
  while (i < n - 1 && r.beats[i + 1] <= beat) i++;
  const j = Math.min(i + 1, n - 1);
  const b0 = r.beats[i], b1 = r.beats[j];
  const tt = b1 > b0 ? (beat - b0) / (b1 - b0) : 0;
  return { x: r.xs[i] + (r.xs[j] - r.xs[i]) * tt, y: r.ys[i] + (r.ys[j] - r.ys[i]) * tt };
}

export default function RiderModel() {
  const groupRef = useRef<THREE.Group>(null);
  const { scene: gltfScene, animations } = useGLTF(RIDER_GLB_PATH);
  const mannequinSettings = useEditorStore(s => s.mannequinSettings);
  // Difficulty changes rarely → a reactive selector here is fine (rebuilds the
  // event schedule); the per-frame work below reads the store imperatively.
  const difficulty = useEditorStore(s =>
    s.beatmap ? s.beatmap.difficulties[s.activeDifficulty] : null,
  );

  // Clone so HMR / remounts don't share skeleton + mixer state with the cache.
  const scene = useMemo(() => {
    const cloned = cloneSkeleton(gltfScene);
    cloned.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        // Skinned meshes can have wrong-looking bounds before the first pose
        // is applied; disable culling so the rider never pops out of view.
        mesh.frustumCulled = false;
        const mat = mesh.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          if (!m) continue;
          m.side = THREE.FrontSide;
          // The visor lens + chest "screen" (`M_Screen`) ship with no texture,
          // base colour or emissive, so they render as a flat grey censor-bar
          // over the eyes. Restyle them as a dark glowing cyan display instead.
          if (/screen/i.test(m.name || '')) {
            m.color.set('#0a1517');
            m.emissive.set('#17b6c7');
            m.emissiveIntensity = 0.8;
            m.metalness = 0.2;
            m.roughness = 0.45;
            continue;
          }
          // Lift very dark imported materials so the rider reads in the
          // dark editor scene without washing out textured parts.
          if (m.emissive && m.emissiveIntensity != null) {
            m.emissiveIntensity = Math.max(m.emissiveIntensity, 0.04);
          }
          // SH_SynthRiders_Characters-style rim/Fresnel glow (cyan-white edge).
          applyRimGlow(m, { color: '#8fe3ff', power: 2.6, strength: 0.45 });
        }
      }
    });
    return cloned;
  }, [gltfScene]);

  const { actions, names } = useAnimations(animations, scene);

  // ── IK bone chains (rail lock): root → mid → end, per limb ──
  const ikBones = useMemo(() => {
    // GLTFLoader strips '.' from node names (arm_stretch.l -> arm_stretchl), so
    // look up by a normalized key (lowercase, alphanumerics only) — resilient to
    // dot/underscore/case differences between the GLB and the runtime scene.
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const byNorm: Record<string, THREE.Object3D> = {};
    scene.traverse((o) => { if (o.name) byNorm[norm(o.name)] = o; });
    const f = (n: string) => byNorm[norm(n)] || undefined;
    // Only hands grind rails in Synth Riders (every note has hand=left/right;
    // low notes are a hand reaching DOWN, not a foot). `clav` = the clavicle,
    // leaned toward the rail before the arm IK to extend reach.
    return {
      handL: { clav: f('shoulder.l'), root: f('arm_stretch.l'), mid: f('forearm_stretch.l'), end: f('hand.l') },
      handR: { clav: f('shoulder.r'), root: f('arm_stretch.r'), mid: f('forearm_stretch.r'), end: f('hand.r') },
    };
  }, [scene]);

  // ── Scale + ground offset from the bind-pose bounds (match DancerModel) ──
  const { modelScale, yOffset } = useMemo(() => {
    scene.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(scene);
    const h = box.max.y - box.min.y;
    if (h <= 0) return { modelScale: 1, yOffset: 0 };
    const s = (1.78 * mannequinSettings.figureHeight) / h;
    return { modelScale: s, yOffset: -box.min.y * s };
  }, [scene, mannequinSettings.figureHeight]);

  // ── Reaction schedules (rebuilt when the difficulty/map changes) ──
  const noteEvents = useMemo(
    () => buildNoteEvents(difficulty?.notes, names),
    [difficulty?.notes, names],
  );
  const obstacleEvents = useMemo(
    () => buildObstacleEvents(difficulty, names),
    [difficulty, names],
  );

  // ── Air-rail sections (floor→air) ──
  // A long multi-node rail is the game's green "air ribbon": the rider launches,
  // stays airborne for the whole section, then lands. We drive the JumpLong arc
  // (Start → Loop → End) across each span, which overrides note/obstacle hits.
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const airSpans = useMemo<Array<[number, number]>>(() => {
    const spans: Array<[number, number]> = [];
    const list = difficulty?.notes;
    if (!Array.isArray(list)) return spans;
    for (const n of list) {
      if (!n.has_rail || !n.rail_nodes || n.rail_nodes.length < 4) continue;
      const last = n.rail_nodes[n.rail_nodes.length - 1];
      const endBeat = (last[2] / 0.02 / 1000) * (bpm / 60); // matches RailLine
      spans.push([n.time_beats, endBeat]);
    }
    return spans;
  }, [difficulty?.notes, bpm]);
  const airClips = useMemo(() => ({
    start: names.find(n => n.toLowerCase().endsWith('jumplong_start')) || null,
    // The middle phase GRINDS the rail (the game snaps the rider to the air rail
    // and plays a Grind clip); fall back to the free-air JumpLong_Loop.
    loop: names.find(n => n.toLowerCase().endsWith('grind_1'))
      || names.find(n => n.toLowerCase().endsWith('jumplong_loop')) || null,
    end: names.find(n => n.toLowerCase().endsWith('jumplong_end')) || null,
  }), [names]);

  // Per air-rail curve: beats + grid X/Y of the head note and every rail node,
  // so the rider can sample its position along the rail at the current beat
  // (the editor equivalent of the game's continuous grind-to-rail translation).
  const airRails = useMemo(() => {
    const out: Array<{ beats: number[]; xs: number[]; ys: number[] }> = [];
    const list = difficulty?.notes;
    if (!Array.isArray(list)) return out;
    for (const n of list) {
      if (!n.has_rail || !n.rail_nodes || n.rail_nodes.length < 4) continue;
      const beats = [n.time_beats];
      const xs = [n.position[0]];
      const ys = [n.position[1]];
      for (const node of n.rail_nodes) {
        beats.push((node[2] / 0.02 / 1000) * (bpm / 60));
        xs.push(node[0]);
        ys.push(node[1]);
      }
      out.push({ beats, xs, ys });
    }
    return out;
  }, [difficulty?.notes, bpm]);

  // ── Per-hand rails (rail lock) ──
  // Every rail that isn't a long "air" ribbon, classified by the HAND that grinds
  // it. Synth Riders has no foot notes — every note (including the low ones) has
  // hand=left/right and is hit by that hand reaching up/down/across. Points are
  // stored in WORLD coords (where the rail sits at the trigger line) so the IK
  // can lock the hand bone onto them while the rail passes.
  const limbRails = useMemo(() => {
    const out: { handL: LimbRail[]; handR: LimbRail[] } = { handL: [], handR: [] };
    const list = difficulty?.notes;
    if (!Array.isArray(list)) return out;
    for (const n of list) {
      if (!n.has_rail || !n.rail_nodes || !n.rail_nodes.length) continue;
      let limb: keyof typeof out | null = null;
      if (n.hand === 'left') limb = 'handL';
      else if (n.hand === 'right') limb = 'handR';
      else continue; // one-hand/special long rails are the air ribbons (body follows)
      const beats = [n.time_beats];
      const xs = [n.position[0] * GRID_W];
      const ys = [n.position[1] * GRID_W + RAIL_Y_BASE];
      for (const node of n.rail_nodes) {
        beats.push((node[2] / 0.02 / 1000) * (bpm / 60));
        xs.push(node[0] * GRID_W);
        ys.push(node[1] * GRID_W + RAIL_Y_BASE);
      }
      out[limb].push({ beats, xs, ys });
    }
    return out;
  }, [difficulty?.notes, bpm]);

  // Grid X/Y on the active air rail at `beat` (linear-interpolated), or null.
  const sampleAirRail = (beat: number): { x: number; y: number } | null => {
    for (const r of airRails) {
      const n = r.beats.length;
      if (beat < r.beats[0] || beat > r.beats[n - 1]) continue;
      let i = 0;
      while (i < n - 1 && r.beats[i + 1] < beat) i++;
      const j = Math.min(i + 1, n - 1);
      const b0 = r.beats[i], b1 = r.beats[j];
      const t = b1 > b0 ? (beat - b0) / (b1 - b0) : 0;
      return { x: r.xs[i] + (r.xs[j] - r.xs[i]) * t, y: r.ys[i] + (r.ys[j] - r.ys[i]) * t };
    }
    return null;
  };

  // ── Imperative animation driver ──
  // activeName: the clip currently faded in. We only crossfade when the target
  // changes, so dense maps don't restart clips every frame.
  const activeName = useRef<string | null>(null);
  const noteIdx = useRef(0);
  const obsIdx = useRef(0);
  const lastBeat = useRef(Number.NEGATIVE_INFINITY);

  // Re-init the driver whenever the action/event set changes (map/skeleton swap).
  useEffect(() => {
    activeName.current = null;
    noteIdx.current = 0;
    obsIdx.current = 0;
    lastBeat.current = Number.NEGATIVE_INFINITY;
  }, [actions, noteEvents, obstacleEvents]);

  const fadeTo = (name: string | null, loopOnce: boolean) => {
    if (!name || name === activeName.current) return;
    const next = actions[name];
    if (!next) return;
    if (loopOnce) {
      next.reset();
      next.setLoop(THREE.LoopOnce, 1);
      next.clampWhenFinished = true;
    } else {
      next.setLoop(THREE.LoopRepeat, Infinity);
    }
    next.enabled = true;
    next.setEffectiveTimeScale(1);
    next.setEffectiveWeight(1);
    next.play();
    const prevName = activeName.current;
    const prev = prevName ? actions[prevName] : null;
    if (prev && prev !== next) next.crossFadeFrom(prev, FADE, true);
    else next.fadeIn(FADE);
    activeName.current = name;
  };

  /** Most recent event in [cb - hold, cb + LEAD]; advances its pointer past stale ones. */
  const pickActive = (
    events: AnimEvent[],
    idxRef: React.MutableRefObject<number>,
    cb: number,
    hold: number,
  ): AnimEvent | null => {
    if (!events.length) return null;
    while (idxRef.current < events.length && events[idxRef.current].beat < cb - hold) {
      idxRef.current++;
    }
    let i = idxRef.current;
    let chosen: AnimEvent | null = null;
    while (i < events.length && events[i].beat <= cb + LEAD_BEATS) {
      chosen = events[i];
      i++;
    }
    return chosen;
  };

  // If the playhead is inside an air-rail span, pick the JumpLong arc phase:
  // Start (launch) at the front, End (landing) at the back, Loop in between.
  const activeAirClip = (cb: number): { clip: string; loopOnce: boolean } | null => {
    for (let i = 0; i < airSpans.length; i++) {
      const [a, b] = airSpans[i];
      if (cb < a || cb > b) continue;
      if (cb < a + AIR_START_BEATS && airClips.start) return { clip: airClips.start, loopOnce: true };
      if (cb > b - AIR_END_BEATS && airClips.end) return { clip: airClips.end, loopOnce: true };
      if (airClips.loop) return { clip: airClips.loop, loopOnce: false };
      if (airClips.start) return { clip: airClips.start, loopOnce: false };
      return null;
    }
    return null;
  };

  useFrame(() => {
    if (!names.length) return;
    const st = useEditorStore.getState();
    const cb = st.playback.currentBeat;
    const playing = st.playback.playing;
    const baseName = (playing ? RUN_CLIPS : IDLE_CLIPS)
      .map(p => names.find(n => n.toLowerCase().includes(p.toLowerCase())))
      .find(Boolean) || names[0];

    // First frame: snap the base loop on without a crossfade.
    if (activeName.current == null) {
      const a = actions[baseName];
      if (a) {
        a.reset();
        a.setLoop(THREE.LoopRepeat, Infinity);
        a.setEffectiveWeight(1);
        a.play();
        activeName.current = baseName;
      }
    }

    // Resync the event pointers after a seek / scrub jump.
    if (Math.abs(cb - lastBeat.current) > 0.5) {
      noteIdx.current = lowerBound(noteEvents, cb - NOTE_HOLD_BEATS);
      obsIdx.current = lowerBound(obstacleEvents, cb - OBSTACLE_HOLD_BEATS);
    }
    lastBeat.current = cb;

    let targetClip = baseName;
    let targetIsHit = false;

    if (playing) {
      // Air rail (floor→air) is top priority: the JumpLong arc holds the rider
      // airborne for the whole section. Otherwise obstacles override notes.
      const air = activeAirClip(cb);
      if (air) {
        targetClip = air.clip;
        targetIsHit = air.loopOnce;
      } else {
        const obs = pickActive(obstacleEvents, obsIdx, cb, OBSTACLE_HOLD_BEATS);
        const note = pickActive(noteEvents, noteIdx, cb, NOTE_HOLD_BEATS);
        const chosen = obs || note;
        if (chosen) {
          targetClip = chosen.clip;
          targetIsHit = true;
        }
      }
    }

    fadeTo(targetClip, targetIsHit);

    // ── Mount + follow the air rail ──
    // While inside an air rail, ease the rider's X/Y onto the rail's current
    // point (it climbs the ribbon and weaves with it); otherwise return to the
    // centred, grounded resting position. Smooth lerp = the game's grind tween.
    const g = groupRef.current;
    if (g) {
      let targetX = 0;
      let targetY = yOffset;
      const pt = playing ? sampleAirRail(cb) : null;
      if (pt) {
        targetX = pt.x * GRID_W;
        const railWorldY = pt.y * GRID_W + RAIL_Y_BASE;
        targetY = yOffset + railWorldY * AIR_MOUNT_LIFT;
      }
      g.position.x += (targetX - g.position.x) * AIR_FOLLOW_LERP;
      g.position.y += (targetY - g.position.y) * AIR_FOLLOW_LERP;
    }
  });

  // ── Rail lock: clavicle lean + 2-bone arm IK pinning the hand onto a rail ──
  // Separate useFrame (registered after the driver above + after drei's mixer
  // tick) so it overrides the baked pose. For each hand, if a rail is active at
  // the current beat, lean the clavicle toward it then lock the hand bone onto
  // the rail's world point.
  const railTargetRef = useRef(new THREE.Vector3());
  const _solR = useRef(new THREE.Quaternion());
  const _solM = useRef(new THREE.Quaternion());
  // Last applied IK pose per hand, for temporal smoothing (kills the per-frame
  // wobble caused by the body animation swaying the shoulder in/out of reach).
  const lastIK = useRef<Record<string, { r: THREE.Quaternion; m: THREE.Quaternion } | undefined>>({});
  const IK_SMOOTH = 0.4; // 0..1 — fraction eased toward the new solution each frame
  useFrame(() => {
    if (!names.length) return;
    const st = useEditorStore.getState();
    if (!st.playback.playing) return;
    const cb = st.playback.currentBeat;
    const hands: Array<keyof typeof ikBones> = ['handL', 'handR'];
    for (const hand of hands) {
      const chain = ikBones[hand];
      if (!chain.root || !chain.mid || !chain.end) continue;
      let pos: { x: number; y: number } | null = null;
      const rails = limbRails[hand];
      for (let i = 0; i < rails.length; i++) {
        const s = sampleLimbRail(rails[i], cb);
        if (s) { pos = s; break; }
      }
      if (!pos) { lastIK.current[hand] = undefined; continue; } // released → free
      railTargetRef.current.set(pos.x, pos.y, 0);
      // Clavicle lean first (extends reach + counters torso sway), then arm IK.
      if (chain.clav) aimBonePartial(chain.clav, chain.root, railTargetRef.current, 0.6);
      solveTwoBoneIK(chain.root, chain.mid, chain.end, railTargetRef.current);
      // Ease from last frame's applied pose toward this solution → smooth, stable.
      _solR.current.copy(chain.root.quaternion);
      _solM.current.copy(chain.mid.quaternion);
      let store = lastIK.current[hand];
      if (store) {
        chain.root.quaternion.copy(store.r).slerp(_solR.current, IK_SMOOTH);
        chain.mid.quaternion.copy(store.m).slerp(_solM.current, IK_SMOOTH);
      } else {
        store = { r: new THREE.Quaternion(), m: new THREE.Quaternion() };
        lastIK.current[hand] = store;
      }
      store.r.copy(chain.root.quaternion);
      store.m.copy(chain.mid.quaternion);
    }
  });

  return (
    <group
      ref={groupRef}
      position={[0, yOffset, FIGURE_Z]}
      rotation={[0, Math.PI, 0]}
      scale={[modelScale, modelScale, modelScale]}
    >
      <primitive object={scene} />
    </group>
  );
}
